import { useLayoutEffect, useState, type CSSProperties, type RefObject } from "react";

/**
 * Where to draw a popover so that **no container can clip it**.
 *
 * A menu positioned `absolute` inside a card is cut off by the card's
 * `overflow: hidden`, and a tip inside one of Home's scrolling boxes is cut off
 * by the box — found on the Settings chair list the first time the ⋯ menu
 * opened. `fixed` against the window escapes every one of them, so the panel is
 * placed from its trigger's own rectangle instead.
 *
 * **It follows its trigger on scroll and resize** rather than closing. Closing
 * was the first version, and it made the menu unusable wherever the page moved
 * even a pixel between opening it and choosing — a browser scrolling the item
 * into view, a list settling — so the item vanished under the pointer.
 *
 * `align` is which edge of the trigger the panel lines up with — a menu opens
 * under the ⋯ and grows leftward, a tip centres under its ?, and a list that
 * belongs to a label (Staff's date, ST9) opens under it and grows rightward.
 * Either way the panel is kept inside the window with an 8px margin.
 */
export function useAnchor(
  open: boolean,
  anchor: RefObject<HTMLElement | null>,
  align: "end" | "center" | "start",
): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(undefined);
      return;
    }

    const place = (): void => {
      const rect = anchor.current?.getBoundingClientRect();
      if (rect === undefined) return;
      const margin = 8;
      const gap = 6;

      /*
       * **Down if there is room, up if there is more room up there.** A row
       * near the bottom of the window opened its ⋯ downward past the edge, and
       * a `fixed` panel cannot be scrolled to — its last item, *Retire*, was
       * simply unreachable. Whichever side it opens on, it is capped to the
       * room that side has and scrolls inside itself beyond that.
       */
      const below = window.innerHeight - rect.bottom - gap - margin;
      const above = rect.top - gap - margin;
      const vertical: CSSProperties =
        below >= 220 || below >= above
          ? { top: rect.bottom + gap, bottom: "auto", maxHeight: below }
          : // `top: auto` matters: the stylesheet gives the panel a default
            // `top` below its trigger, and with both set the upward menu sat
            // off the bottom of the window.
            { top: "auto", bottom: window.innerHeight - rect.top + gap, maxHeight: above };

      if (align === "end") {
        setStyle({
          position: "fixed",
          ...vertical,
          right: Math.max(margin, window.innerWidth - rect.right),
          overflowY: "auto",
        });
      } else if (align === "start") {
        setStyle({
          position: "fixed",
          ...vertical,
          left: Math.max(margin, Math.min(rect.left, window.innerWidth - margin - 280)),
          right: "auto",
          overflowY: "auto",
        });
      } else {
        const centre = rect.left + rect.width / 2;
        setStyle({
          position: "fixed",
          ...vertical,
          left: Math.min(Math.max(margin, centre), window.innerWidth - margin),
          transform: "translateX(-50%)",
          overflowY: "auto",
        });
      }
    };

    place();
    window.addEventListener("resize", place);
    // Capture, so a scroll inside any box — not only the page — moves it too.
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, anchor, align]);

  return style;
}
